/**
 * Favicon + document.title + PWA app-icon badge.
 *
 * Updates the browser tab title (e.g. "(3) SMeet") and overlays a red counter
 * on the favicon so users see new messages even when the tab is inactive.
 *
 * On installed PWAs (macOS dock, Windows taskbar, Android Chrome launcher)
 * also drives the OS-level app icon badge via the Web Badging API. The SW
 * (push-sw.js) updates the same badge when the app is closed; this handles
 * the open-app case where setUnreadBadge is called from the React tree on
 * every unread-count change.
 */

const BADGE_FAVICON_SIZE = 64;
const FAVICON_SOURCE = "/logo192.png";

let cachedOriginalTitle = "";
let cachedOriginalFaviconHref = "";
let faviconImage: HTMLImageElement | null = null;
let pendingCount: number | null = null;

function getOriginalTitle(): string {
  if (!cachedOriginalTitle) {
    const match = document.title.match(/^\(\S+\)\s+(.+)$/);
    cachedOriginalTitle = match ? match[1] : document.title;
  }
  return cachedOriginalTitle;
}

function getFaviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  if (!cachedOriginalFaviconHref) {
    cachedOriginalFaviconHref = link.href || "/favicon.ico";
  }
  return link;
}

function renderBadgedFavicon(count: number): string | null {
  if (!faviconImage || !faviconImage.complete || faviconImage.naturalWidth === 0) {
    return null;
  }
  const canvas = document.createElement("canvas");
  canvas.width = BADGE_FAVICON_SIZE;
  canvas.height = BADGE_FAVICON_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.drawImage(faviconImage, 0, 0, BADGE_FAVICON_SIZE, BADGE_FAVICON_SIZE);

  const label = count > 99 ? "99+" : String(count);
  const isWide = label.length > 1;
  const radius = isWide ? 22 : 18;
  const cx = BADGE_FAVICON_SIZE - radius - 2;
  const cy = radius + 2;

  ctx.fillStyle = "#e53935";
  ctx.beginPath();
  if (isWide) {
    ctx.ellipse(cx, cy, radius, radius - 4, 0, 0, Math.PI * 2);
  } else {
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  }
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${isWide ? 22 : 26}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, cx, cy + 1);

  return canvas.toDataURL("image/png");
}

function updateAppBadge(count: number): void {
  // Web Badging API — types augmented in shims-badging.d.ts. Browsers without
  // support (Firefox, Safari <16.4) silently no-op via the existence check.
  // The OS persists the badge across PWA restarts.
  if (typeof navigator === "undefined") return;
  try {
    if (count <= 0) {
      if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
      else if (navigator.setAppBadge) navigator.setAppBadge(0).catch(() => {});
    } else if (navigator.setAppBadge) {
      navigator.setAppBadge(Math.min(count, 99)).catch(() => {});
    }
  } catch { /* badging API unavailable — ignore */ }
}

export function setUnreadBadge(count: number): void {
  const safeCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  const base = getOriginalTitle();
  const titleLabel = safeCount > 99 ? "99+" : String(safeCount);
  document.title = safeCount > 0 ? `(${titleLabel}) ${base}` : base;

  updateAppBadge(safeCount);

  const link = getFaviconLink();

  if (safeCount === 0) {
    pendingCount = null;
    link.href = cachedOriginalFaviconHref;
    return;
  }

  if (!faviconImage) {
    faviconImage = new Image();
    pendingCount = safeCount;
    faviconImage.onload = () => {
      // pendingCount may have been cleared by a count→0 transition that
      // happened while the image was still loading; ignore stale loads.
      if (pendingCount && pendingCount > 0) {
        const url = renderBadgedFavicon(pendingCount);
        if (url) link.href = url;
      }
    };
    faviconImage.onerror = () => {
      faviconImage = null;
      pendingCount = null;
    };
    faviconImage.src = FAVICON_SOURCE;
    return;
  }

  if (faviconImage.complete && faviconImage.naturalWidth > 0) {
    pendingCount = null;
    const url = renderBadgedFavicon(safeCount);
    if (url) link.href = url;
  } else {
    pendingCount = safeCount;
  }
}
